import React, { useEffect, useMemo, useState } from 'react';
import {
  createOrganization,
  createUser,
  getIntegrations,
  getNotifications,
  getOrganizations,
  getRoles,
  getUsers,
  updateUser
} from '../services/api';

function Tabs({active, setActive}){
  const items = [
    {key:'orgs', label:'Organizations'},
    {key:'users', label:'Users & Roles'},
    {key:'integrations', label:'Integrations'},
    {key:'notifications', label:'Notifications'}
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(it=> (
        <button key={it.key} onClick={()=>setActive(it.key)} className={`px-3 py-2 rounded-lg border transition-all ${active===it.key? 'bg-blue-600 text-white border-blue-600':'bg-white text-gray-800 border-gray-200 hover:bg-gray-100'}`}>{it.label}</button>
      ))}
    </div>
  );
}

export default function AdminConsole(){
  const [active, setActive] = useState('orgs');
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(()=>{ getOrganizations().then(setOrgs); },[]);
  useEffect(()=>{ getUsers().then(setUsers); },[]);
  useEffect(()=>{ getRoles().then(setRoles); },[]);
  useEffect(()=>{ getIntegrations().then(setIntegrations); },[]);
  useEffect(()=>{ getNotifications({ limit: 50 }).then(setNotifications); },[]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Administrative Console</div>
            <div className="text-2xl font-extrabold">Configure & Govern</div>
          </div>
          <Tabs active={active} setActive={setActive} />
        </div>
      </div>

      {active==='orgs' && <OrganizationsPanel orgs={orgs} setOrgs={setOrgs} />}
      {active==='users' && <UsersRolesPanel users={users} setUsers={setUsers} roles={roles} />}
      {active==='integrations' && <IntegrationsPanel integrations={integrations} />}
      {active==='notifications' && <NotificationsPanel notifications={notifications} />}
    </div>
  );
}

function OrganizationsPanel({ orgs, setOrgs }){
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  const addOrg = async () => {
    if(!name?.trim()) return;
    const created = await createOrganization({ name, domain });
    setOrgs([...(orgs||[]), created]);
    setName(''); setDomain('');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
        <div className="font-semibold mb-2">Organizations</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {orgs?.map(o=> (
            <div key={o.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="font-medium text-gray-900">{o.name}</div>
              <div className="text-sm text-gray-600">{o.domain}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
        <div className="font-semibold mb-2">Add Organization</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Name" className="w-full mb-2 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"/>
        <input value={domain} onChange={e=>setDomain(e.target.value)} placeholder="Domain" className="w-full mb-3 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"/>
        <button onClick={addOrg} className="w-full px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all">Create</button>
      </div>
    </div>
  );
}

function UsersRolesPanel({ users, setUsers, roles }){
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const selectedUser = useMemo(()=> users?.find(u=>u.id===selectedUserId) || null, [users, selectedUserId]);

  const addUser = async () => {
    if(!name?.trim() || !email?.trim()) return;
    const created = await createUser({ name, email, organization_id: users?.[0]?.organization_id || 1 });
    setUsers([...(users||[]), created]);
    setName(''); setEmail('');
  };

  const rename = async () => {
    if(!selectedUserId || !name?.trim()) return;
    const updated = await updateUser(selectedUserId, { name });
    setUsers(users?.map(u=>u.id===selectedUserId? { ...u, name: updated.name }: u));
    setName('');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
        <div className="font-semibold mb-2">Users</div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Title</th>
              </tr>
            </thead>
            <tbody>
              {users?.map(u=> (
                <tr key={u.id} className={`border-t border-gray-200 hover:bg-gray-50 ${selectedUserId===u.id? 'bg-blue-50':''}`}>
                  <td className="py-2 pr-4 font-medium text-gray-900"><button onClick={()=>setSelectedUserId(u.id)} className="hover:underline">{u.name}</button></td>
                  <td className="py-2 pr-4">{u.email}</td>
                  <td className="py-2 pr-4">{u.title || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
        <div className="font-semibold mb-2">Quick Actions</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Name" className="w-full mb-2 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"/>
        <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" className="w-full mb-3 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"/>
        <div className="flex gap-2">
          <button onClick={addUser} className="flex-1 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all">Add</button>
          <button onClick={rename} className="flex-1 px-3 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-all">Rename Selected</button>
        </div>
        <div className="mt-4 text-sm text-gray-700">
          <div className="font-medium mb-1">Roles</div>
          <div className="flex flex-wrap gap-2">
            {roles?.map(r=> (
              <span key={r.id} className="text-xs px-2 py-0.5 rounded-full border bg-gray-50 border-gray-200 text-gray-800">{r.name}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function IntegrationsPanel({ integrations }){
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
      <div className="font-semibold mb-3">Connectors</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {integrations?.map(i=> (
          <div key={i.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="font-medium text-gray-900">{i.name}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${i.status==='connected'?'text-green-700 border-green-200 bg-green-50':'text-red-700 border-red-200 bg-red-50'}`}>{i.status}</span>
            </div>
            <div className="text-xs text-gray-600 mt-1">Last sync: {new Date(i.last_sync).toLocaleString()}</div>
            <div className="mt-2 flex gap-2">
              <button className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all">Test</button>
              <button className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-all">Settings</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotificationsPanel({ notifications }){
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
      <div className="font-semibold mb-3">Recent Notifications</div>
      <div className="space-y-2">
        {notifications?.map(n=> (
          <div key={n.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex items-start justify-between">
            <div>
              <div className="text-sm text-gray-900">{n.message}</div>
              <div className="text-xs text-gray-600">{n.type} • {new Date(n.created_at).toLocaleString()}</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${n.read? 'text-gray-700 border-gray-200 bg-white':'text-blue-700 border-blue-200 bg-blue-50'}`}>{n.read? 'read':'new'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
